// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Haggle — the bootstrap. Owns the screens and the room, and no game logic whatsoever.
//
// The room is joined ONCE and held until the player goes back to the menu. Every deal after the
// first — every "play again" — happens inside it, versioned by rematch.ts. Leaving and re-joining to
// "reset" hands back the room object Trystero is still tearing down, and both players then sit in
// the right room code, alone, for ever.

import '@ben-gy/game-engine/mobile.css';
import { hardenViewport } from '@ben-gy/game-engine/mobile';
import { createNet, roomAppId, setTurnConfig, type Net, type PeerId } from '@ben-gy/game-engine/net';
import { getTurnConfig } from '@ben-gy/game-engine/turn';
import { createRounds, type Rounds } from '@ben-gy/game-engine/rematch';
import {
  clearRoomInUrl,
  createLobby,
  createRoomEntry,
  mintCode,
  normalizeRoomCode,
  setRoomInUrl,
  type Lobby,
} from '@ben-gy/game-engine/lobby';
import { createSfx } from '@ben-gy/game-engine/sound';
import { createStore } from '@ben-gy/game-engine/storage';
import { newSeed } from '@ben-gy/game-engine/rng';
import { DEFAULT_TIER, TIERS, TIER_IDS, tierOf, type TierId } from './bot';
import { DEFAULT_MODE, MODES, MODE_IDS, modeOf, type ModeId } from './modes';
import { runCountdown } from './countdown';
import { Session } from './session';
import { Table, emptySel } from './table';
import { summarise } from './view';
import { FOOTER_HTML, HOW_HTML, aboutHtml, menuHtml, openPanel, summaryHtml } from './ui';
import { MAX_DEALS, matchOver, matchWinner, newMatch, recordDeal, starterFor, type MatchState } from './game';
import { WARE_NAME } from './wares';
import { other, scoreOf, type PublicEvent, type Seat } from './rules';
import './styles/main.css';

const SLUG = 'haggle';
const RIVALS = ['Wren', 'Corvin', 'Mable', 'Tobin'];

const app = document.getElementById('app')!;
const store = createStore(SLUG);
const sfx = createSfx({
  muted: store.get<boolean>('muted', false) ?? false,
  // Every cue is chosen so the table can be read with your eyes shut: a take is a soft click, a sale
  // climbs, a bounty flourishes, the bell falls, and a dry column is a flat dead note.
  patches: {
    tap: { type: 'square', freq: [480, 480], dur: 0.03, gain: 0.12 },
    take: { type: 'triangle', freq: [420, 620], dur: 0.09, gain: 0.18 },
    hire: { type: 'sine', freq: [220, 160], dur: 0.14, gain: 0.2 },
    sell: { type: 'triangle', freq: [560, 1180], dur: 0.26, gain: 0.26 },
    bounty: { type: 'sine', freq: [880, 1660], dur: 0.34, gain: 0.28 },
    bell: { type: 'sawtooth', freq: [300, 120], dur: 0.3, gain: 0.24 },
    dryx: { type: 'square', freq: [180, 120], dur: 0.22, gain: 0.2 },
    tick: { type: 'triangle', freq: [660, 660], dur: 0.08, gain: 0.2 },
    go: { type: 'triangle', freq: [880, 1320], dur: 0.22, gain: 0.26 },
    over: { type: 'triangle', freq: [520, 300], dur: 0.5, gain: 0.24 },
  },
});
const play = (n: string): void => {
  try {
    sfx.play(n);
  } catch {
    /* audio is decoration; never let it take the game down */
  }
};

interface Settings {
  mode: ModeId;
  tier: TierId;
}
const settings: Settings = {
  mode: modeOf(store.get<string>('mode', DEFAULT_MODE)).id,
  tier: tierOf(store.get<string>('tier', DEFAULT_TIER)).id,
};

interface RoundOpts {
  mode: string;
}

let net: Net | null = null;
let rounds: Rounds<RoundOpts> | null = null;
let lobby: Lobby | null = null;
let session: Session | null = null;
let table: Table | null = null;
let roomCode = '';
let iMintedIt = false;
let beat: ReturnType<typeof setInterval> | null = null;
let ticker: ReturnType<typeof setInterval> | null = null;
let countdown: { cancel: () => void } | null = null;
let deepLinkUsed = false;
let match: MatchState = newMatch();
let sendWire: (m: unknown, to?: string) => void = () => {};

// ── screens ───────────────────────────────────────────────────────────────────────────────────────

const setPlaying = (on: boolean): void => {
  document.body.classList.toggle('playing', on);
};

function clearScreen(): void {
  countdown?.cancel();
  countdown = null;
  table?.destroy();
  table = null;
  if (ticker) clearInterval(ticker);
  ticker = null;
  app.replaceChildren();
}

function showMenu(): void {
  clearScreen();
  setPlaying(false);
  const best = store.get<number>(`best:${settings.mode}`, 0) ?? 0;
  app.innerHTML = menuHtml(settings.mode, settings.tier, sfx.muted(), best);

  app.querySelectorAll<HTMLButtonElement>('[data-mode]').forEach((b) =>
    b.addEventListener('click', () => {
      settings.mode = modeOf(b.dataset.mode).id;
      store.set('mode', settings.mode);
      showMenu();
    }),
  );
  app.querySelectorAll<HTMLButtonElement>('[data-tier]').forEach((b) =>
    b.addEventListener('click', () => {
      settings.tier = tierOf(b.dataset.tier).id;
      store.set('tier', settings.tier);
      showMenu();
    }),
  );
  app.querySelector('#playSolo')!.addEventListener('click', () => {
    sfx.unlock();
    play('tap');
    match = newMatch();
    startSolo();
  });
  app.querySelector('#playFriends')!.addEventListener('click', () => {
    sfx.unlock();
    play('tap');
    showRoomEntry();
  });
  app.querySelector('#howBtn')!.addEventListener('click', () => openPanel(app, HOW_HTML));
  app.querySelector('#aboutBtn')!.addEventListener('click', () => openPanel(app, aboutHtml()));
  const mute = app.querySelector<HTMLButtonElement>('#muteBtn')!;
  mute.addEventListener('click', () => {
    sfx.setMuted(!sfx.muted());
    store.set('muted', sfx.muted());
    mute.textContent = sfx.muted() ? 'Sound off' : 'Sound on';
    mute.setAttribute('aria-pressed', String(sfx.muted()));
  });

  if (!store.get<boolean>('seenHow', false)) {
    store.set('seenHow', true);
    openPanel(app, HOW_HTML);
  }
}

// ── solo ──────────────────────────────────────────────────────────────────────────────────────────

function startSolo(): void {
  if (matchOver(match)) match = newMatch();
  const seed = newSeed();
  buildSession({
    mode: settings.mode,
    seed,
    round: match.results.length + 1,
    mySeat: 0,
    botSeat: 1,
    isHost: true,
    names: ['You', RIVALS[Math.floor(Math.random() * RIVALS.length)]],
    peerOfSeat: ['', ''],
    starter: starterFor(seed, match),
  });
}

// ── the room ──────────────────────────────────────────────────────────────────────────────────────

function showRoomEntry(): void {
  clearScreen();
  setPlaying(false);
  const host = document.createElement('div');
  host.className = 'main-content';
  app.append(host);
  createRoomEntry({
    container: host,
    title: 'Play with a friend',
    subtitle: 'Start a room and send the link — or scan the code, or type it in.',
    onSubmit: (code, created) => {
      roomCode = normalizeRoomCode(code);
      iMintedIt = created;
      match = newMatch();
      enterRoom();
    },
    onCancel: () => showMenu(),
  });
  app.insertAdjacentHTML('beforeend', FOOTER_HTML);
}

function enterRoom(): void {
  clearScreen();
  setRoomInUrl(roomCode);
  net = createNet(
    { appId: roomAppId(SLUG), roomId: roomCode, claimHost: iMintedIt },
    {
      onPeers: () => {
        // Never navigate from here: this fires in the lobby AND just after a deal ends.
        lobby?.repaint();
      },
      onPeerLeave: (id: PeerId) => {
        session?.peerLeft(id);
        lobby?.repaint();
        repaint();
      },
      onHostChange: () => {
        lobby?.repaint();
        repaint();
      },
    },
  );

  const wire = net.channel<unknown>('hag', (m, from) => session?.receive(m as never, from));
  const priv = net.channel<unknown>('hageye', (m, from) => session?.receive(m as never, from));
  sendWire = (m, to) => (to === undefined ? wire(m) : priv(m, to as PeerId));

  rounds = createRounds<RoundOpts>({
    net,
    playerName: store.get<string>('name', '') || `Trader ${Math.floor(Math.random() * 90 + 10)}`,
    minPlayers: 2,
    roundOpts: () => ({ mode: settings.mode }),
    onRound: (info) => {
      // The HOST's pick is what the room plays, frozen inside the start, so two peers can never be
      // on different markets.
      const mode = modeOf(info.opts?.mode).id;
      if (!info.seated) {
        showSpectator();
        return;
      }
      const mySeat = info.players.findIndex((p) => p.id === net!.selfId) as Seat;
      startRound({
        mode,
        seed: info.seed,
        round: info.round,
        mySeat,
        botSeat: null,
        isHost: info.isHost,
        names: info.players.map((p) => p.name),
        peerOfSeat: info.players.map((p) => p.id),
        starter: starterFor(info.seed, match),
      });
    },
    onChange: () => lobby?.repaint(),
  });

  showLobby();
}

function showLobby(): void {
  clearScreen();
  setPlaying(false);
  const host = document.createElement('div');
  host.className = 'main-content';
  app.append(host);
  lobby = createLobby({
    container: host,
    net: net!,
    rounds: rounds!,
    roomCode,
    minPlayers: 2,
    maxPlayers: 2,
    modeSlot: () => {
      const st = rounds!.state();
      const chosen = st.isHost ? settings.mode : modeOf(st.hostOpts?.mode).id;
      return `<div class="modeslot"><span class="modeslot-label">Market</span>
        <div class="segmented small">${MODE_IDS.map(
          (id) =>
            `<button type="button" class="seg${id === chosen ? ' on' : ''}" data-lmode="${id}"${st.isHost ? '' : ' disabled'}>${MODES[id].name}</button>`,
        ).join('')}</div>
        <p class="fine">${st.isHost ? MODES[settings.mode].blurb : 'The host picks the market.'}</p></div>`;
    },
    onModeMount: () => {
      host.querySelectorAll<HTMLButtonElement>('[data-lmode]').forEach((b) =>
        b.addEventListener('click', () => {
          settings.mode = modeOf(b.dataset.lmode).id;
          store.set('mode', settings.mode);
          lobby?.repaint();
        }),
      );
    },
    onCancel: () => leaveRoom(),
  });
  app.insertAdjacentHTML('beforeend', FOOTER_HTML);
}

function showSpectator(): void {
  clearScreen();
  setPlaying(false);
  app.innerHTML = `<div class="main-content"><h2>A deal is already under way</h2>
    <p class="fine">You are in the next one. Hang on — it will start on its own.</p></div>${FOOTER_HTML}`;
}

/**
 * Leaving is the ONLY place the room is torn down, and it clears `?room=` on the way out. Leave it
 * in the URL and a reload — or reopening from a home-screen icon — silently drags the player back
 * into a room they left, with no way to start a new one.
 */
function leaveRoom(): void {
  if (beat) clearInterval(beat);
  beat = null;
  session?.destroy();
  session = null;
  rounds?.destroy();
  rounds = null;
  lobby?.destroy();
  lobby = null;
  void net?.leave();
  net = null;
  match = newMatch();
  clearRoomInUrl();
  deepLinkUsed = true;
  showMenu();
}

// ── a deal ────────────────────────────────────────────────────────────────────────────────────────

interface StartArgs {
  mode: ModeId;
  seed: number;
  round: number;
  mySeat: Seat;
  botSeat: Seat | null;
  isHost: boolean;
  names: string[];
  peerOfSeat: string[];
  starter: Seat;
}

function startRound(args: StartArgs): void {
  clearScreen();
  setPlaying(true);
  const holder = document.createElement('div');
  holder.className = 'countwrap';
  app.append(holder);
  countdown = runCountdown({
    container: holder,
    sfx: play,
    onDone: () => {
      holder.remove();
      buildSession(args);
    },
  });
}

function buildSession(args: StartArgs): void {
  clearScreen();
  setPlaying(true);
  table = new Table({
    submit: (a) => {
      session?.submit(a);
      table?.clearSelection();
      repaint();
    },
    sfx: play,
    onHelp: () => openPanel(app, HOW_HTML),
    onQuit: () => {
      // Back to the lobby, never out of the room — and `rounds.finish()` FIRST, because the engine
      // lobby paints nothing at all while the round is still 'playing' and this seat is seated.
      if (net) {
        rounds?.finish();
        showLobby();
      } else showMenu();
    },
  });
  app.append(table.root);

  session = new Session({
    mode: MODES[args.mode],
    round: args.round,
    mySeat: args.mySeat,
    botSeat: args.botSeat,
    skill: TIERS[settings.tier],
    isHost: args.isHost,
    names: args.names,
    peerOfSeat: [...args.peerOfSeat],
    seed: args.seed,
    starter: args.starter,
    pennants: [...match.pennants] as [number, number],
    deps: {
      send: (m, to) => sendWire(m, to),
      onChange: () => repaint(),
      onEvents: (evs) => narrate(evs, args.names, args.mySeat),
      onEnd: () => showResults(),
    },
  });

  if (beat) clearInterval(beat);
  // setInterval, never rAF: a backgrounded tab must keep its heartbeat alive and must still land an
  // armed walkover, or the survivor sits on a live board for ever.
  beat = setInterval(() => session?.beat(), 2000);
  ticker = setInterval(() => {
    session?.tick();
    if (session?.walkoverIn() !== null) repaint();
  }, 500);
  repaint();
}

function repaint(): void {
  const s = session;
  if (!table || !s || !s.pub) return;
  const you = s.mySeat ?? 0;
  const wo = s.walkoverIn();
  const note =
    wo !== null
      ? `Your rival left the room — closing this deal in ${Math.ceil(wo / 1000)}s.`
      : s.note || defaultNote(s.pub.toMove === you);
  table.render({
    p: s.pub,
    you,
    satchel: s.satchel,
    names: s.names,
    pennants: s.pennants,
    yourTurn: s.pub.toMove === you && !s.pub.over && s.mySeat !== null,
    note,
  });
}

const defaultNote = (mine: boolean): string =>
  mine ? 'Take, hire, barter or sell — one of the four.' : 'Your rival is thinking.';

function narrate(evs: readonly PublicEvent[], names: readonly string[], you: Seat): void {
  const s = session;
  for (const e of evs) {
    const who = (seat: Seat): string => (seat === you ? 'You' : (names[seat] ?? 'They'));
    if (e.k === 'take') {
      play('take');
      s && (s.note = `${who(e.seat)} took ${WARE_NAME[e.ware]}.`);
    } else if (e.k === 'hire') {
      play('hire');
      s && (s.note = `${who(e.seat)} hired ${e.n} porter${e.n === 1 ? '' : 's'}.`);
    } else if (e.k === 'barter') {
      play('take');
      s && (s.note = `${who(e.seat)} bartered ${e.took.length} card${e.took.length === 1 ? '' : 's'}.`);
    } else if (e.k === 'sell') {
      play(e.bounty > 0 ? 'bounty' : 'sell');
      table?.flyChits(e.ware, e.chits);
      const total = e.chits.reduce((a, b) => a + b, 0);
      s &&
        (s.note = `${who(e.seat)} sold ${e.cards} ${WARE_NAME[e.ware]} for ${total}${e.bounty > 0 ? ` plus a ${e.bounty} bounty` : ''}.`);
      if (s?.pub && s.pub.tariffs[e.ware].length === 0) play('dryx');
    } else if (e.k === 'bell') {
      play('bell');
      s &&
        (s.note = e.ware
          ? `The bell burned the ${e.chit} off ${WARE_NAME[e.ware]}.`
          : 'The bell rang, and there was nothing left to burn.');
      table?.bump();
    } else if (e.k === 'dump') {
      s && (s.note = `${who(e.seat)} had no play and dropped a card.`);
    } else if (e.k === 'end') {
      play('over');
    }
  }
}

// ── the summary ───────────────────────────────────────────────────────────────────────────────────

function showResults(): void {
  const s = session;
  if (!s || !s.pub) return;
  setPlaying(false);
  const winner = s.winner();
  recordDeal(match, winner);
  s.pennants = [...match.pennants] as [number, number];
  const you = s.mySeat ?? 0;
  if (!net && s.mySeat !== null) {
    const best = store.get<number>(`best:${s.mode.id}`, 0) ?? 0;
    const mine = scoreOf(s.pub, s.mySeat).total;
    if (mine > best) store.set(`best:${s.mode.id}`, mine);
  }
  const sum = summarise(s.pub, s.satchelsForSummary(), s.names, winner);
  const done = matchOver(match);
  const mw = matchWinner(match);
  const line = done
    ? mw === null
      ? 'The match is level.'
      : `${mw === you ? 'You win' : `${s.names[mw] ?? 'Your rival'} wins`} the match ${match.pennants[mw]}–${match.pennants[other(mw)]}.`
    : `Deal ${match.results.length} of ${MAX_DEALS}.`;

  clearScreen();
  app.innerHTML = summaryHtml(sum, you, s.pennants, line, net !== null);

  const again = app.querySelector<HTMLButtonElement>('#againBtn')!;
  const toLobby = app.querySelector<HTMLButtonElement>('#lobbyBtn')!;
  const wait = app.querySelector<HTMLElement>('#resWait')!;
  if (done) again.textContent = 'New match';

  if (net && rounds) {
    rounds.finish();
    toLobby.hidden = false;
    toLobby.addEventListener('click', () => showLobby());
    again.addEventListener('click', () => {
      if (matchOver(match)) match = newMatch();
      rounds!.vote();
      again.disabled = true;
      again.textContent = 'Waiting for your rival…';
    });
    // A visible reason and a horizon. One player still reading the summary must not hold the room.
    ticker = setInterval(() => {
      const st = rounds?.state();
      if (!st) return;
      wait.hidden = false;
      wait.textContent =
        st.startsInMs !== null
          ? `Next deal in ${Math.ceil(st.startsInMs / 1000)}s…`
          : `${st.votes.length} of ${st.present.length} ready.`;
    }, 400);
  } else {
    again.addEventListener('click', () => {
      play('tap');
      if (matchOver(match)) match = newMatch();
      startSolo();
    });
  }
  app.querySelector('#menuBtn')!.addEventListener('click', () => (net ? leaveRoom() : showMenu()));
  app
    .querySelector('.results-feedback')!
    .addEventListener('click', (e) => window.feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement }));
}

// ── boot ──────────────────────────────────────────────────────────────────────────────────────────

async function boot(): Promise<void> {
  hardenViewport();
  // ONCE, before any mesh exists. Trystero builds one global connection pool from the first room's
  // config, so a later call leaves the initiating half of every pair STUN-only.
  try {
    setTurnConfig(await getTurnConfig());
  } catch {
    /* fails open — TURN is an upgrade, never a dependency */
  }

  // A `?room=` is honoured ONCE. After that, "play with a friend" always offers create-or-join, so a
  // reload can never drag somebody back into a room they have left.
  const deep = new URL(location.href).searchParams.get('room');
  if (deep && !deepLinkUsed) {
    deepLinkUsed = true;
    roomCode = normalizeRoomCode(deep);
    iMintedIt = false;
    enterRoom();
    return;
  }
  showMenu();
}

window.addEventListener('beforeunload', () => void net?.leave());
void boot();

// Exposed for the browser verification pass: drives a deal synchronously in a tab where rAF and real
// timers are throttled. Never used by the game itself.
(window as unknown as Record<string, unknown>).__haggle = {
  state: () => session?.pub,
  satchel: () => session?.satchel,
  submit: (a: unknown) => session?.submit(a as never),
  step: () => session?.nudge(),
  tick: () => session?.tick(),
  sel: () => emptySel(),
  mint: () => mintCode(),
  tiers: TIER_IDS.map((t) => TIERS[t].name),
  modes: MODE_IDS,
};
